import { memo, useContext, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { PanelMenu } from "primereact/panelmenu"
import { UserContext } from "../../context/userContext"
import useAdditionalServices from "../../hooks/useAdditionalServices"
import useMainMenu from "../../hooks/useMainMenu"
import useTemplateAvailability from "../../hooks/useTemplateAvailability"
import { useMicroservices } from "../../hooks/useMicroservices"
import {
  buildPanelMenuModel,
  renderMaterialIconMenuItemTemplate,
} from "./components/helpers/menuBuilder"
import baseStyles from "../../styles/SidebarPanelMenuBase.module.css"

// Default sidebar metadata by service label (used when config does not provide values).
const SERVICE_META_BY_LABEL = {
  "Coordination Boards": {
    path: "/coordination-board",
    icon: "grid_view",
  },
  Documents: {
    path: null,
    icon: "text_snippet",
  },
  "External Calls": {
    path: "/external-calls",
    icon: "phone",
  },
  Housing: {
    path: null,
    icon: "house",
  },
  Messages: {
    path: "/global-messages",
    icon: "chat",
  },
  "Referral Network": {
    path: "/referral-network",
    icon: "group",
  },
}

function AdditionalServicesSidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const userCtx = useContext(UserContext)
  const { isMicroserviceEnabled } = useMicroservices()

  // Match NavMain template-refresh triggers so service visibility stays consistent.
  const templatesReloadDeps = useMemo(
    () => [userCtx.session.menu, userCtx.session.ux],
    [userCtx.session.menu, userCtx.session.ux],
  )

  // Used to filter configured Services children by template availability parity.
  const { isTemplateAvailable } = useTemplateAvailability({
    reloadDeps: templatesReloadDeps,
  })

  // Pull only the configured Services section and shared path resolver.
  const { menuItems: configuredServicesSections, resolveMenuPath } =
    useMainMenu({
      includeLabels: ["Services"],
    })

  // Mirror NavMain service visibility so this sidebar stays in sync with feature toggles.
  const sidebarToggles = useMemo(
    () => ({
      documentsEnabled: isMicroserviceEnabled(8),
      documentsOpen: false,
      telerecovery: isMicroserviceEnabled(1),
      housingEnabled: isMicroserviceEnabled(7),
      housingOpen: false,
    }),
    [isMicroserviceEnabled],
  )

  // Build a Services-only base model from config (with template-availability filtering)
  // so the shared hook can merge app-defined items against the same source NavMain uses.
  const configuredServicesMenuModel = useMemo(
    () =>
      configuredServicesSections.map((item) => ({
        label: item?.label,
        icon: item?.icon,
        items: Array.isArray(item?.items)
          ? item.items.filter((child) =>
              isTemplateAvailable({ template_title: child?.label }),
            )
          : [],
      })),
    [configuredServicesSections, isTemplateAvailable],
  )

  // Merge configured Services menu items with in-app fallback service definitions
  // so sidebar output matches top nav and still works when config omits details.
  const { mergedAdditionalServicesItems } = useAdditionalServices({
    staffRole: userCtx.session.user.staff_role,
    toggles: sidebarToggles,
    navigate,
    menuModel: configuredServicesMenuModel,
  })

  // For services without routes (Documents/Housing), map clicks to overlay actions
  // so these items behave correctly in sidebar without navigation paths.
  const overlayAwareCommandByLabel = useMemo(
    () => ({
      Documents: () => userCtx.showDocumentRequests(),
      Housing: () => {
        const housingContext = userCtx.housing?.context || {}
        userCtx.housing?.setHousing({
          ...housingContext,
          showHousing: true,
        })
      },
    }),
    [userCtx],
  )

  // Convert service entries into PanelMenu leaf items, preserving command behavior.
  const menuItems = useMemo(
    () =>
      mergedAdditionalServicesItems
        .map((item) => ({
          label: item.label,
          path:
            (typeof item.path === "string" && item.path
              ? resolveMenuPath(item.path)
              : null) || SERVICE_META_BY_LABEL[item.label]?.path,
          icon: item.icon || SERVICE_META_BY_LABEL[item.label]?.icon,
          command: overlayAwareCommandByLabel[item.label] || item.command,
        }))
        .filter((item) => item.path || typeof item.command === "function"),
    [
      mergedAdditionalServicesItems,
      overlayAwareCommandByLabel,
      resolveMenuPath,
    ],
  )

  // Build active-state-aware PanelMenu model using the shared sidebar menu helper.
  const additionalServicesMenuModel = useMemo(
    () =>
      buildPanelMenuModel({
        items: menuItems,
        pathname: location.pathname,
        navigate,
        itemTemplate: renderMaterialIconMenuItemTemplate,
        activeClassName: baseStyles.menuLinkActiveItem,
      }),
    [location.pathname, menuItems, navigate],
  )

  if (!additionalServicesMenuModel.length) {
    return null
  }

  return (
    <div className={baseStyles.menuRoot}>
      <PanelMenu
        key={`additional-services-menu-${location.pathname}`}
        model={additionalServicesMenuModel}
        multiple={false}
      />
    </div>
  )
}

export default memo(AdditionalServicesSidebar)
